import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tokenManager } from '../api/apiClient';
import config from '../config';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Función para refrescar el token usando el endpoint /api/usuarios/refresh-token
   */
  const refreshToken = useCallback(async () => {
    const currentToken = tokenManager.getToken();
    if (!currentToken) {
      console.log('⚠️ No hay token para refrescar');
      return false;
    }

    try {
      console.log('🔄 Solicitando refresh de token...');
      const response = await axios.post(
        `${config.API_BASE_URL}/api/usuarios/refresh-token`,
        { token: currentToken }
      );

      const newToken = response.data.token;
      tokenManager.setToken(newToken);

      // Actualizar user en state con el nuevo token
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userData.token = newToken;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }

      console.log('✅ Token refrescado exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error al refrescar token:', error.response?.data || error.message);

      // Solo hacer logout si el refresh falló por token inválido
      // No hacer logout por errores de red temporales
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🚪 Token inválido - Cerrando sesión');
        logout();
      }

      return false;
    }
  }, []);

  /**
   * Cargar usuario al iniciar la aplicación
   * Solo carga de localStorage, el token se validará en la primera petición
   */
  useEffect(() => {
    const loadUserData = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          console.log('✅ Usuario cargado de localStorage:', userData.username || userData.email);
          setUser(userData);
        } catch (error) {
          console.error('❌ Error parseando usuario de localStorage:', error);
          // Si el localStorage está corrupto, limpiarlo
          localStorage.removeItem('user');
          setUser(null);
        }
      } else {
        console.log('ℹ️ No hay usuario en localStorage');
      }
      setLoading(false);
    };

    loadUserData();
  }, []);

  /**
   * Auto-refresh preventivo cada 45 minutos
   * El token expira en 1 hora, refrescamos antes para evitar cortes
   * 45 minutos da una ventana de seguridad de 15 minutos
   */
  useEffect(() => {
    if (!user) return;

    // Auto-refresh periódico cada 45 minutos
    const interval = setInterval(() => {
      console.log('🔄 Auto-refresh de token preventivo (cada 45 min)');
      refreshToken();
    }, 45 * 60 * 1000); // 45 minutos en milisegundos

    return () => clearInterval(interval);
  }, [user, refreshToken]);

  /**
   * Refresh de token al detectar actividad del usuario
   * Si el usuario interactúa y han pasado más de 30 minutos desde el último refresh,
   * renovamos el token proactivamente
   */
  useEffect(() => {
    if (!user) return;

    let lastRefreshTime = Date.now();

    const handleUserActivity = () => {
      const timeSinceLastRefresh = Date.now() - lastRefreshTime;
      const thirtyMinutes = 30 * 60 * 1000;

      // Si han pasado más de 30 minutos, refrescar token
      if (timeSinceLastRefresh > thirtyMinutes) {
        console.log('👆 Actividad detectada después de 30+ min - Refrescando token');
        refreshToken().then(() => {
          lastRefreshTime = Date.now();
        });
      }
    };

    // Eventos de actividad del usuario
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle para no ejecutar en cada evento
    let throttleTimeout;
    const throttledHandler = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          handleUserActivity();
          throttleTimeout = null;
        }, 5000); // Verificar máximo cada 5 segundos
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledHandler);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledHandler);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [user, refreshToken]);

  /**
   * Refresh de token al volver de hibernación/suspensión
   * Si la pestaña estuvo inactiva por más de 30 minutos, refrescar al volver
   */
  useEffect(() => {
    if (!user) return;

    let lastVisibilityChange = Date.now();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // La pestaña volvió a ser visible
        const timeSinceLastCheck = Date.now() - lastVisibilityChange;
        const thirtyMinutes = 30 * 60 * 1000;

        if (timeSinceLastCheck > thirtyMinutes) {
          console.log('👁️ Pestaña visible después de 30+ min - Refrescando token');
          refreshToken();
        }
      }
      lastVisibilityChange = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, refreshToken]);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = (redirect = true) => {
    console.log('🚪 Iniciando logout - Limpiando sesión...');

    // Limpiar state
    setUser(null);

    // Limpiar token manager
    tokenManager.clearToken();

    // Limpiar localStorage completamente
    localStorage.removeItem('user');
    localStorage.removeItem('token');

    // Limpiar sessionStorage
    sessionStorage.clear();

    console.log('✅ Sesión limpiada completamente');

    // Redirigir a login si estamos en el navegador y se solicita
    if (redirect && typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
      console.log('🔄 Redirigiendo a /login...');
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar el contexto Auth en cualquier componente
export const useAuth = () => useContext(AuthContext);
